import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client'
import { SetContextLink } from '@apollo/client/link/context'

import { currentLanguage } from './language.js'

/**
 * Where the API lives.
 *
 * The same API the product talks to. There is no admin backend and no admin
 * database: administration is a set of operations on one GraphQL schema, gated
 * on an Admin profile the server alone can grant.
 */
export const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:4000'

/**
 * `credentials: 'include'`, because the session is an httpOnly cookie — the
 * same one the product uses, issued by the same API. That is why the API's
 * CORS allowlist has to name this origin as well (`ADMIN_APP_URL`): a
 * credentialed request from an origin it does not know is refused, which is
 * the behaviour you want everywhere except the two origins an operator
 * configured.
 *
 * There is no token for this client to hold and nothing to put in an
 * Authorization header.
 */
export function createApolloClient(): ApolloClient {
  return new ApolloClient({
    link: new SetContextLink((previous) => ({
      ...previous,
      headers: {
        ...(previous.headers as Record<string, string> | undefined),
        'accept-language': currentLanguage(),
      },
    })).concat(new HttpLink({ uri: `${API_URL}/graphql`, credentials: 'include' })),
    cache: new InMemoryCache(),
  })
}

/**
 * Signing out. A POST, not a link: Better Auth's endpoint refuses a GET.
 *
 * There is deliberately no sign-*in* endpoint here. An administrator signs in
 * through the product, with the same account and the same three ways in, and
 * arrives at this application with a session already. Reimplementing the
 * sign-in screen would be a second implementation of the one flow in the
 * product that must not have two.
 */
export const SIGN_OUT_ENDPOINT = `${API_URL}/api/auth/sign-out`

/**
 * Where to send somebody who is not signed in, or who is signed in without an
 * Admin profile.
 *
 * The product's own sign-in screen. Baked in at build time like `VITE_API_URL`,
 * so a deployment points its admin bundle at its own product origin.
 */
export const WEB_APP_URL: string = import.meta.env.VITE_WEB_APP_URL ?? 'http://localhost:5173'
